import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Plus, Users as UsersIcon, Star, FileText, TrendingUp } from 'lucide-react';
import { useContacts } from '../../hooks/useContacts';
import ContactCard from './ContactCard';
import AddContactModal from './AddContactModal';
import EditContactModal from './EditContactModal';

const Contacts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    contacts,
    stats,
    loading,
    error,
    fetchContacts,
    createContact,
    updateContact,
    deleteContact,
    toggleFavorite
  } = useContacts();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [highlightedContactId, setHighlightedContactId] = useState(null);

  // Check for action parameter to open add modal
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'add') {
      setShowAddModal(true);
      // Remove the action parameter from URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Check for highlight parameter to highlight specific contact
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId) {
      setHighlightedContactId(highlightId);
      // Auto-remove highlight after 5 seconds
      const timer = setTimeout(() => {
        setHighlightedContactId(null);
        // Remove highlight parameter from URL
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('highlight');
        setSearchParams(newParams, { replace: true });
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams]);

  // Filter contacts based on search and filter type
  const filteredContacts = contacts.filter(contact => {
    // Search filter
    const matchesSearch = searchQuery === '' || 
      contact.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchQuery.toLowerCase());

    // Type filter
    const matchesFilter = 
      filterType === 'all' ||
      (filterType === 'favorites' && contact.is_favorite) ||
      (filterType === 'recent' && true) || // All contacts are recent
      (filterType === 'invoice_count' && contact.invoice_count > 0);

    return matchesSearch && matchesFilter;
  });

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length > 2 || query.length === 0) {
      await fetchContacts({ search: query, filter: filterType });
    }
  };

  const handleFilterChange = async (filter) => {
    setFilterType(filter);
    await fetchContacts({ search: searchQuery, filter });
  };

  const handleAddContact = async (contactData) => {
    await createContact(contactData);
    setShowAddModal(false);
  };

  const handleEditContact = async (contactId, contactData) => {
    await updateContact(contactId, contactData);
    setEditingContact(null);
  };

  const handleDeleteContact = async (contactId) => {
    await deleteContact(contactId);
  };

  const handleToggleFavorite = async (contact) => {
    await toggleFavorite(contact);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <UsersIcon className="w-6 h-6" />
            Contacts
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your invoice recipients and business contacts
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm font-medium shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Add Contact
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <UsersIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-blue-900">{stats.total_contacts || 0}</p>
                <p className="text-xs text-blue-700">Total Contacts</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-600 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-yellow-900">{stats.favorite_contacts || 0}</p>
                <p className="text-xs text-yellow-700">Favorites</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-green-900">{stats.contacts_with_invoices || 0}</p>
                <p className="text-xs text-green-700">With Invoices</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-purple-900">
                  £{parseFloat(stats.total_invoiced || 0).toFixed(2)}
                </p>
                <p className="text-xs text-purple-700">Total Invoiced</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search contacts by name, company, or email..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Show:</label>
            <select
              value={filterType}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="all">All Contacts</option>
              <option value="favorites">Favorites</option>
              <option value="recent">Recent</option>
              <option value="invoice_count">With Invoices</option>
            </select>
          </div>
        </div>
      </div>

      {/* Contact List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading contacts...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700">{error}</p>
        </div>
      ) : filteredContacts.length === 0 ? (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-sm border border-gray-200">
          <div className="text-center py-12 px-6">
            <UsersIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchQuery || filterType !== 'all' ? 'No contacts found' : 'No contacts yet'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {searchQuery || filterType !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Get started by adding your first contact'}
            </p>
            {!searchQuery && filterType === 'all' && (
              <button
                onClick={() => setShowAddModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Your First Contact
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredContacts.map(contact => (
            <ContactCard
              key={contact.id}
              contact={contact}
              onEdit={setEditingContact}
              onDelete={handleDeleteContact}
              onToggleFavorite={handleToggleFavorite}
              isHighlighted={highlightedContactId === contact.id}
            />
          ))}
        </div>
      )}

      {/* Pagination Info */}
      {filteredContacts.length > 0 && (
        <div className="text-center text-sm text-gray-600">
          Showing {filteredContacts.length} of {contacts.length} contacts
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddContactModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddContact}
        />
      )}

      {editingContact && (
        <EditContactModal
          contact={editingContact}
          onClose={() => setEditingContact(null)}
          onSave={handleEditContact}
        />
      )}
    </div>
  );
};

export default Contacts;

